import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { stripePromise, createPaymentIntent } from '../services/stripe'
import { supabase } from '../services/supabase'

interface ShippingAddress {
  email: string
  fullName: string
  streetAddress: string
  city: string
  state: string
  postalCode: string
  country: string
  phone: string
}

interface OrderConfirmationEmailData {
  email: string
  orderId: string
  customerName: string
  items: Array<{ name: string; quantity: number; price: number }>
  subtotal: number
  shippingCost: number
  taxAmount: number
  totalAmount: number
  shippingAddress: {
    street_address: string
    city: string
    state: string
    postal_code: string
    country: string
  }
}

// Send order confirmation email via Edge Function (non-blocking)
async function sendOrderConfirmationEmail(data: OrderConfirmationEmailData): Promise<void> {
  console.log('Sending order confirmation email to:', data.email)

  try {
    const { data: responseData, error } = await supabase.functions.invoke('send-order-confirmation', {
      body: data,
    })

    if (error) {
      console.error('Email service error:', error)
    } else {
      console.log('Order confirmation email response:', responseData)
    }
  } catch (err) {
    console.error('Failed to send order confirmation email:', err)
  }
}

type CheckoutStep = 'shipping' | 'payment' | 'review'

// Payment Form Component (inside Elements provider)
function PaymentForm({
  onPaymentSuccess,
  onBack,
  isProcessing,
  setIsProcessing,
  paymentError,
  setPaymentError,
  finalTotal
}: {
  onPaymentSuccess: (paymentIntentId: string) => void
  onBack: () => void
  isProcessing: boolean
  setIsProcessing: (v: boolean) => void
  paymentError: string | null
  setPaymentError: (e: string | null) => void
  finalTotal: number
}) {
  const stripe = useStripe()
  const elements = useElements()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      setPaymentError('Stripe has not loaded yet. Please wait...')
      return
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/order-confirmation',
        },
        redirect: 'if_required',
      })

      if (error) {
        setPaymentError(error.message || 'Payment failed. Please try again.')
        setIsProcessing(false)
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaymentSuccess(paymentIntent.id)
      } else {
        setPaymentError('Payment was not completed. Please try again.')
        setIsProcessing(false)
      }
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'An unexpected error occurred')
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 text-gray-500"
              style={{ width: '20px', height: '20px' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
              />
            </svg>
            Payment Details
          </h2>
        </div>

        <div className="p-5">
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />

          {paymentError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                  style={{ width: '20px', height: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
                <p className="text-sm text-red-700">{paymentError}</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onBack}
              disabled={isProcessing}
              className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 font-medium text-sm rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={!stripe || !elements || isProcessing}
              className={`flex-1 py-3 px-4 font-semibold text-sm rounded-xl transition-colors flex items-center justify-center gap-2 ${
                isProcessing || !stripe
                  ? 'bg-purple-400 text-white cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isProcessing ? (
                <>
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Processing...
                </>
              ) : (
                <>
                  Pay ${finalTotal.toFixed(2)}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Secure Payment Badge */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-4 h-4"
          style={{ width: '16px', height: '16px' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
        Payments secured by Stripe
      </div>
    </form>
  )
}

export default function Checkout() {
  const { user } = useAuth()
  const { cartItems, loading, cartTotal, clearCart } = useCart()
  const navigate = useNavigate()

  // Step management
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('shipping')

  // Shipping address form state
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    email: user?.email || '',
    fullName: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: ''
  })
  const [formErrors, setFormErrors] = useState<Partial<ShippingAddress>>({})
  const [formSubmitted, setFormSubmitted] = useState(false)

  // Payment state
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [_paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Order state
  const [orderError, setOrderError] = useState<string | null>(null)
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)

  // Discount code state
  const [discountCode, setDiscountCode] = useState('')
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id: string
    code: string
    type: 'percentage' | 'fixed'
    value: number
    amount: number
  } | null>(null)
  const [discountError, setDiscountError] = useState<string | null>(null)
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false)

  // Calculate costs
  const subtotal = cartTotal
  const discountAmount = appliedDiscount?.amount || 0
  const discountedSubtotal = subtotal - discountAmount
  const shippingCost = discountedSubtotal >= 100 ? 0 : 10 // Free shipping over $100 (after discount)
  const taxRate = 0.10 // 10% tax
  const taxAmount = discountedSubtotal * taxRate // Tax on discounted amount
  const finalTotal = discountedSubtotal + shippingCost + taxAmount

  // Update a single field
  const updateField = (field: keyof ShippingAddress, value: string) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Validate the form
  const validateForm = (): boolean => {
    const errors: Partial<ShippingAddress> = {}

    // Email validation (required for guests, optional but validated for logged-in users)
    if (!user) {
      if (!shippingAddress.email.trim()) {
        errors.email = 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingAddress.email.trim())) {
        errors.email = 'Please enter a valid email address'
      }
    } else if (shippingAddress.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingAddress.email.trim())) {
      errors.email = 'Please enter a valid email address'
    }

    if (!shippingAddress.fullName.trim()) {
      errors.fullName = 'Full name is required'
    }
    if (!shippingAddress.streetAddress.trim()) {
      errors.streetAddress = 'Street address is required'
    }
    if (!shippingAddress.city.trim()) {
      errors.city = 'City is required'
    }
    if (!shippingAddress.state.trim()) {
      errors.state = 'State/Province is required'
    }
    if (!shippingAddress.postalCode.trim()) {
      errors.postalCode = 'Postal/ZIP code is required'
    }
    if (!shippingAddress.country.trim()) {
      errors.country = 'Country is required'
    }
    if (!shippingAddress.phone.trim()) {
      errors.phone = 'Phone number is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Validate and apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return

    setIsValidatingDiscount(true)
    setDiscountError(null)

    const codeToCheck = discountCode.trim().toUpperCase()

    try {
      // 1. Fetch the discount code
      const { data: codeData, error: codeError } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('code', codeToCheck)
        .single()

      if (codeError || !codeData) {
        setDiscountError('Invalid discount code')
        return
      }

      // 2. Check if expired
      if (codeData.expiration_date && new Date(codeData.expiration_date) < new Date()) {
        setDiscountError('This discount code has expired')
        return
      }

      // 3. Check minimum order amount
      if (codeData.minimum_order_amount && subtotal < codeData.minimum_order_amount) {
        setDiscountError(`Minimum order of $${codeData.minimum_order_amount.toFixed(2)} required for this code`)
        return
      }

      // 4. Check if already used by this customer (only for authenticated users)
      if (user) {
        const { data: usageData } = await supabase
          .from('discount_code_uses')
          .select('id')
          .eq('discount_code_id', codeData.id)
          .eq('customer_id', user.id)
          .single()

        if (usageData) {
          setDiscountError('You have already used this discount code')
          return
        }
      }

      // 5. Calculate discount amount
      let calculatedAmount: number
      if (codeData.discount_type === 'percentage') {
        calculatedAmount = subtotal * (codeData.discount_value / 100)
      } else {
        // Fixed discount - cap at subtotal
        calculatedAmount = Math.min(codeData.discount_value, subtotal)
      }

      // 6. Apply the discount
      setAppliedDiscount({
        id: codeData.id,
        code: codeData.code,
        type: codeData.discount_type,
        value: codeData.discount_value,
        amount: calculatedAmount
      })
      setDiscountCode('')
    } catch (error) {
      console.error('Error validating discount code:', error)
      setDiscountError('Failed to validate discount code')
    } finally {
      setIsValidatingDiscount(false)
    }
  }

  // Remove applied discount
  const handleRemoveDiscount = () => {
    setAppliedDiscount(null)
    setDiscountError(null)
  }

  // Check if form is complete
  const isFormComplete =
    // Email required for guests
    (user || (shippingAddress.email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingAddress.email.trim()))) &&
    shippingAddress.fullName.trim() !== '' &&
    shippingAddress.streetAddress.trim() !== '' &&
    shippingAddress.city.trim() !== '' &&
    shippingAddress.state.trim() !== '' &&
    shippingAddress.postalCode.trim() !== '' &&
    shippingAddress.country.trim() !== '' &&
    shippingAddress.phone.trim() !== ''

  // Handle continue to payment
  const handleContinueToPayment = async () => {
    setFormSubmitted(true)
    if (!validateForm()) return

    setIsCreatingPaymentIntent(true)
    setPaymentError(null)

    try {
      // Create payment intent with amount in cents
      const amountInCents = Math.round(finalTotal * 100)
      const customerEmail = user?.email || shippingAddress.email
      const response = await createPaymentIntent({
        amount: amountInCents,
        currency: 'usd',
        metadata: {
          customer_email: customerEmail,
          customer_type: user ? 'registered' : 'guest',
          shipping_name: shippingAddress.fullName,
        }
      })

      setClientSecret(response.clientSecret)
      setPaymentIntentId(response.paymentIntentId)
      setCurrentStep('payment')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Error creating payment intent:', error)
      setPaymentError(
        error instanceof Error
          ? error.message
          : 'Failed to initialize payment. Please try again.'
      )
    } finally {
      setIsCreatingPaymentIntent(false)
    }
  }

  // Handle payment success - create order
  const handlePaymentSuccess = async (confirmedPaymentIntentId: string) => {
    setIsPlacingOrder(true)
    setOrderError(null)

    const isGuestOrder = !user
    const customerEmail = user?.email || shippingAddress.email

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      // For guests, use anon key; for users, use access token
      const storedSession = user ? localStorage.getItem(`sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`) : null
      const accessToken = storedSession ? JSON.parse(storedSession).access_token : supabaseKey

      // Create the order with payment info - different structure for guests vs users
      const orderData: Record<string, unknown> = {
        total_amount: finalTotal,
        status: 'pending',
        payment_intent_id: confirmedPaymentIntentId,
        discount_code_id: appliedDiscount?.id || null,
        discount_amount: discountAmount,
        shipping_address: {
          full_name: shippingAddress.fullName,
          street_address: shippingAddress.streetAddress,
          city: shippingAddress.city,
          state: shippingAddress.state,
          postal_code: shippingAddress.postalCode,
          country: shippingAddress.country,
          phone: shippingAddress.phone,
          email: customerEmail,
          subtotal: subtotal,
          discount_amount: discountAmount,
          shipping_cost: shippingCost,
          tax_amount: taxAmount
        }
      }

      // Add user-specific or guest-specific fields
      if (user) {
        orderData.customer_id = user.id
        orderData.is_guest_order = false
      } else {
        orderData.customer_id = null
        orderData.guest_email = shippingAddress.email
        orderData.guest_name = shippingAddress.fullName
        orderData.is_guest_order = true
      }

      const orderResponse = await fetch(`${supabaseUrl}/rest/v1/orders`, {
        method: 'POST',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(orderData)
      })

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text()
        throw new Error(`Failed to create order: ${errorText}`)
      }

      const [createdOrder] = await orderResponse.json()
      const orderId = createdOrder.id

      // Create order items with variant info snapshot
      const orderItems = cartItems.map(item => ({
        order_id: orderId,
        product_id: item.product_id,
        variant_id: item.variant_id || null,
        variant_name: item.variant?.variant_name || null,
        variant_value: item.variant?.variant_value || null,
        quantity: item.quantity,
        price_at_purchase: item.variant?.price ?? item.product?.price ?? 0
      }))

      const itemsResponse = await fetch(`${supabaseUrl}/rest/v1/order_items`, {
        method: 'POST',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderItems)
      })

      if (!itemsResponse.ok) {
        const errorText = await itemsResponse.text()
        throw new Error(`Failed to create order items: ${errorText}`)
      }

      // Decrement stock for each item and check for low stock alerts
      interface LowStockAlert {
        productName: string
        variantInfo?: string
        currentStock: number
        threshold: number
        productId: string
      }
      const lowStockAlerts: LowStockAlert[] = []

      for (const item of cartItems) {
        if (item.variant_id && item.variant) {
          // Fetch current variant data with threshold info
          const variantResponse = await fetch(
            `${supabaseUrl}/rest/v1/product_variants?id=eq.${item.variant_id}&select=stock_quantity,low_stock_threshold,low_stock_notified`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${accessToken}`,
              }
            }
          )
          const variantData = await variantResponse.json()
          const currentVariant = variantData?.[0]

          const previousStock = currentVariant?.stock_quantity ?? item.variant.stock_quantity
          const threshold = currentVariant?.low_stock_threshold ?? 5
          const wasNotified = currentVariant?.low_stock_notified ?? false

          // Decrement variant stock
          const newStock = Math.max(0, previousStock - item.quantity)
          await fetch(`${supabaseUrl}/rest/v1/product_variants?id=eq.${item.variant_id}`, {
            method: 'PATCH',
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ stock_quantity: newStock })
          })

          // Check if stock just crossed below threshold
          const justCrossedThreshold = previousStock > threshold && newStock <= threshold && newStock > 0

          if (justCrossedThreshold && !wasNotified) {
            lowStockAlerts.push({
              productName: item.product?.name || 'Unknown Product',
              variantInfo: `${item.variant.variant_name}: ${item.variant.variant_value}`,
              currentStock: newStock,
              threshold,
              productId: item.product_id
            })

            // Update low_stock_notified flag
            await fetch(`${supabaseUrl}/rest/v1/product_variants?id=eq.${item.variant_id}`, {
              method: 'PATCH',
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ low_stock_notified: true })
            })
          }

          // Reset notified flag if stock went back above threshold
          if (newStock > threshold && wasNotified) {
            await fetch(`${supabaseUrl}/rest/v1/product_variants?id=eq.${item.variant_id}`, {
              method: 'PATCH',
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ low_stock_notified: false })
            })
          }
        } else if (item.product) {
          // Fetch current product data with threshold info
          const productResponse = await fetch(
            `${supabaseUrl}/rest/v1/products?id=eq.${item.product_id}&select=stock,low_stock_threshold,low_stock_notified`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${accessToken}`,
              }
            }
          )
          const productData = await productResponse.json()
          const currentProduct = productData?.[0]

          const previousStock = currentProduct?.stock ?? item.product.stock
          const threshold = currentProduct?.low_stock_threshold ?? 5
          const wasNotified = currentProduct?.low_stock_notified ?? false

          // Decrement product stock (for products without variants)
          const newStock = Math.max(0, previousStock - item.quantity)
          await fetch(`${supabaseUrl}/rest/v1/products?id=eq.${item.product_id}`, {
            method: 'PATCH',
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ stock: newStock })
          })

          // Check if stock just crossed below threshold
          const justCrossedThreshold = previousStock > threshold && newStock <= threshold && newStock > 0

          if (justCrossedThreshold && !wasNotified) {
            lowStockAlerts.push({
              productName: item.product.name,
              currentStock: newStock,
              threshold,
              productId: item.product_id
            })

            // Update low_stock_notified flag
            await fetch(`${supabaseUrl}/rest/v1/products?id=eq.${item.product_id}`, {
              method: 'PATCH',
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ low_stock_notified: true })
            })
          }

          // Reset notified flag if stock went back above threshold
          if (newStock > threshold && wasNotified) {
            await fetch(`${supabaseUrl}/rest/v1/products?id=eq.${item.product_id}`, {
              method: 'PATCH',
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ low_stock_notified: false })
            })
          }
        }
      }

      // Send low stock alerts (non-blocking)
      if (lowStockAlerts.length > 0) {
        for (const alert of lowStockAlerts) {
          supabase.functions.invoke('send-low-stock-alert', {
            body: {
              productName: alert.productName,
              variantInfo: alert.variantInfo,
              currentStock: alert.currentStock,
              threshold: alert.threshold,
              productId: alert.productId
            }
          }).catch(err => console.error('Failed to send low stock alert:', err))
        }
      }

      // Record discount code usage if a discount was applied (only for authenticated users)
      if (appliedDiscount && user) {
        await fetch(`${supabaseUrl}/rest/v1/discount_code_uses`, {
          method: 'POST',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount_code_id: appliedDiscount.id,
            customer_id: user.id,
            order_id: orderId
          })
        })
      }

      // Send order confirmation email (non-blocking)
      // We don't await this - if it fails, the order is still successful
      const emailToSend = customerEmail
      console.log('Triggering order confirmation email for order:', orderId, 'to email:', emailToSend)

      if (emailToSend) {
        sendOrderConfirmationEmail({
          email: emailToSend,
          orderId: orderId,
          customerName: shippingAddress.fullName,
          items: cartItems.map(item => ({
            name: item.variant
              ? `${item.product?.name || 'Unknown Product'} (${item.variant.variant_name}: ${item.variant.variant_value})`
              : item.product?.name || 'Unknown Product',
            quantity: item.quantity,
            price: item.variant?.price ?? item.product?.price ?? 0
          })),
          subtotal,
          shippingCost,
          taxAmount,
          totalAmount: finalTotal,
          shippingAddress: {
            street_address: shippingAddress.streetAddress,
            city: shippingAddress.city,
            state: shippingAddress.state,
            postal_code: shippingAddress.postalCode,
            country: shippingAddress.country
          }
        })
      } else {
        console.warn('No email address found, skipping confirmation email')
      }

      // For guest orders, store order info in sessionStorage for confirmation page access
      if (isGuestOrder) {
        sessionStorage.setItem('guest_order_id', orderId)
        sessionStorage.setItem('guest_order_email', shippingAddress.email)
      }

      // Clear the cart
      await clearCart()

      // Navigate to order confirmation
      navigate(`/order-confirmation/${orderId}`, { replace: true })

    } catch (error) {
      console.error('Error creating order:', error)
      setOrderError(
        error instanceof Error
          ? error.message
          : 'Payment succeeded but failed to create order. Please contact support.'
      )
      setIsProcessing(false)
    } finally {
      setIsPlacingOrder(false)
    }
  }

  // Go back to shipping
  const handleBackToShipping = () => {
    setCurrentStep('shipping')
    setClientSecret(null)
    setPaymentIntentId(null)
  }

  // Redirect to cart if empty
  useEffect(() => {
    if (!loading && cartItems.length === 0) {
      navigate('/cart', { replace: true })
    }
  }, [loading, cartItems.length, navigate])

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Checkout</h1>
            <p className="text-gray-500 text-sm">Review your order</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading checkout...</p>
          </div>
        </div>
      </div>
    )
  }

  // Empty cart
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <p className="text-gray-500 text-sm">Redirecting to cart...</p>
          </div>
        </div>
      </div>
    )
  }

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          currentStep === 'shipping' ? 'bg-purple-600 text-white' : 'bg-green-500 text-white'
        }`}>
          {currentStep === 'shipping' ? '1' : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <span className={`text-sm font-medium ${currentStep === 'shipping' ? 'text-purple-600' : 'text-green-600'}`}>
          Shipping
        </span>
      </div>
      <div className={`w-12 h-0.5 mx-2 ${currentStep !== 'shipping' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          currentStep === 'payment' ? 'bg-purple-600 text-white' :
          currentStep === 'shipping' ? 'bg-gray-200 text-gray-500' : 'bg-green-500 text-white'
        }`}>
          {currentStep === 'review' ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : '2'}
        </div>
        <span className={`text-sm font-medium ${
          currentStep === 'payment' ? 'text-purple-600' :
          currentStep === 'shipping' ? 'text-gray-500' : 'text-green-600'
        }`}>
          Payment
        </span>
      </div>
    </div>
  )

  // Payment Step
  if (currentStep === 'payment' && clientSecret) {
    return (
      <div className="min-h-screen">
        <div className="max-w-2xl mx-auto px-6 py-10">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={handleBackToShipping}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 mr-1"
                style={{ width: '16px', height: '16px' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Back to Shipping
            </button>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment</h1>
            <p className="text-gray-500 text-sm">Enter your payment details to complete your order</p>
          </div>

          <StepIndicator />

          {/* Order Summary Mini */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Order Total</span>
              <span className="text-lg font-bold text-gray-900">${finalTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Stripe Elements */}
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#9333ea',
                  borderRadius: '8px',
                }
              }
            }}
          >
            <PaymentForm
              onPaymentSuccess={handlePaymentSuccess}
              onBack={handleBackToShipping}
              isProcessing={isProcessing || isPlacingOrder}
              setIsProcessing={setIsProcessing}
              paymentError={paymentError || orderError}
              setPaymentError={setPaymentError}
              finalTotal={finalTotal}
            />
          </Elements>
        </div>
      </div>
    )
  }

  // Shipping Form Step
  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/cart"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
              style={{ width: '16px', height: '16px' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back to Cart
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Checkout</h1>
          <p className="text-gray-500 text-sm">Enter your shipping details</p>
        </div>

        <StepIndicator />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Items & Shipping Form */}
          <div className="lg:col-span-2">
            {/* Guest Checkout Banner */}
            {!user && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0"
                    style={{ width: '20px', height: '20px' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900">Checking out as guest</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      You can complete your purchase without an account.{' '}
                      <Link to="/login" className="font-medium underline hover:text-blue-800">
                        Sign in
                      </Link>
                      {' '}or{' '}
                      <Link to="/signup" className="font-medium underline hover:text-blue-800">
                        create an account
                      </Link>
                      {' '}to track orders and save addresses.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">
                  Order Items ({cartItems.length})
                </h2>
              </div>

              <div className="divide-y divide-gray-100">
                {cartItems.map((item) => {
                  const itemPrice = item.variant?.price ?? item.product?.price ?? 0
                  const itemKey = item.variant_id ? `${item.product_id}-${item.variant_id}` : item.product_id
                  return (
                    <div key={itemKey} className="p-4">
                      <div className="flex gap-4">
                        <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden border border-gray-100 flex-shrink-0">
                          {item.product?.image_urls && item.product.image_urls.length > 0 ? (
                            <img
                              src={item.product.image_urls[0]}
                              alt={item.product.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1}
                                stroke="currentColor"
                                className="w-6 h-6 text-gray-300"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                />
                              </svg>
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                            {item.product?.name || 'Unknown Product'}
                          </h3>
                          {item.variant && (
                            <p className="text-xs text-purple-600 mt-0.5">
                              {item.variant.variant_name}: {item.variant.variant_value}
                            </p>
                          )}
                          <p className="text-sm text-gray-500 mt-1">
                            Qty: {item.quantity} x ${itemPrice.toFixed(2)}
                          </p>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-gray-900">
                            ${(itemPrice * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Shipping Address Form */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-gray-500"
                    style={{ width: '20px', height: '20px' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                    />
                  </svg>
                  Shipping Address
                </h2>
              </div>

              <div className="p-5">
                <div className="space-y-4">
                  {/* Email Address - shown for both guests and logged-in users */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address {!user && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={shippingAddress.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="you@example.com"
                      readOnly={!!user}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        formErrors.email && formSubmitted
                          ? 'border-red-300 bg-red-50'
                          : user
                          ? 'border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                    {formErrors.email && formSubmitted && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>
                    )}
                    {!user && (
                      <p className="mt-1.5 text-xs text-gray-500">
                        Order confirmation will be sent to this email
                      </p>
                    )}
                  </div>

                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      value={shippingAddress.fullName}
                      onChange={(e) => updateField('fullName', e.target.value)}
                      placeholder="John Doe"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        formErrors.fullName && formSubmitted
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                    {formErrors.fullName && formSubmitted && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.fullName}</p>
                    )}
                  </div>

                  {/* Street Address */}
                  <div>
                    <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="streetAddress"
                      value={shippingAddress.streetAddress}
                      onChange={(e) => updateField('streetAddress', e.target.value)}
                      placeholder="123 Main Street, Apt 4B"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        formErrors.streetAddress && formSubmitted
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                    {formErrors.streetAddress && formSubmitted && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.streetAddress}</p>
                    )}
                  </div>

                  {/* City and State Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1.5">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        placeholder="Riyadh"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                          formErrors.city && formSubmitted
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      />
                      {formErrors.city && formSubmitted && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.city}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1.5">
                        State / Province <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="state"
                        value={shippingAddress.state}
                        onChange={(e) => updateField('state', e.target.value)}
                        placeholder="Riyadh Province"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                          formErrors.state && formSubmitted
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      />
                      {formErrors.state && formSubmitted && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.state}</p>
                      )}
                    </div>
                  </div>

                  {/* Postal Code and Country Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Postal / ZIP Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="postalCode"
                        value={shippingAddress.postalCode}
                        onChange={(e) => updateField('postalCode', e.target.value)}
                        placeholder="12345"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                          formErrors.postalCode && formSubmitted
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      />
                      {formErrors.postalCode && formSubmitted && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.postalCode}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="country"
                        value={shippingAddress.country}
                        onChange={(e) => updateField('country', e.target.value)}
                        placeholder="Saudi Arabia"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                          formErrors.country && formSubmitted
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      />
                      {formErrors.country && formSubmitted && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.country}</p>
                      )}
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={shippingAddress.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="+966 50 123 4567"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        formErrors.phone && formSubmitted
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                    {formErrors.phone && formSubmitted && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 sticky top-24">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Order Summary</h2>

              <div className="space-y-3 pb-4 border-b border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900 font-medium">${subtotal.toFixed(2)}</span>
                </div>
                {appliedDiscount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 flex items-center gap-1">
                      Discount ({appliedDiscount.code})
                      <button
                        type="button"
                        onClick={handleRemoveDiscount}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove discount"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                    <span className="text-green-600 font-medium">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Shipping</span>
                  {shippingCost === 0 ? (
                    <span className="text-green-600 font-medium">FREE</span>
                  ) : (
                    <span className="text-gray-900 font-medium">${shippingCost.toFixed(2)}</span>
                  )}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax (10%)</span>
                  <span className="text-gray-900 font-medium">${taxAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Discount Code Input */}
              {!appliedDiscount && (
                <div className="py-4 border-b border-gray-100">
                  <label htmlFor="discountCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="discountCode"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value.toUpperCase())
                        setDiscountError(null)
                      }}
                      placeholder="Enter code"
                      className="min-w-0 flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase"
                    />
                    <button
                      type="button"
                      onClick={handleApplyDiscount}
                      disabled={!discountCode.trim() || isValidatingDiscount}
                      className={`flex-shrink-0 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        discountCode.trim() && !isValidatingDiscount
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isValidatingDiscount ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
                      ) : (
                        'Apply'
                      )}
                    </button>
                  </div>
                  {discountError && (
                    <p className="mt-2 text-xs text-red-500">{discountError}</p>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center py-4 border-b border-gray-100">
                <span className="text-base font-semibold text-gray-900">Total</span>
                <span className="text-xl font-bold text-gray-900">${finalTotal.toFixed(2)}</span>
              </div>

              {/* Free Shipping Notice */}
              {shippingCost === 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-green-600 flex-shrink-0"
                      style={{ width: '20px', height: '20px' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                      />
                    </svg>
                    <p className="text-sm text-green-700 font-medium">
                      You qualify for FREE shipping!
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Error */}
              {paymentError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                      style={{ width: '20px', height: '20px' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      />
                    </svg>
                    <p className="text-sm text-red-700">{paymentError}</p>
                  </div>
                </div>
              )}

              {/* Continue Button */}
              <button
                onClick={handleContinueToPayment}
                disabled={!isFormComplete || isCreatingPaymentIntent}
                className={`w-full mt-4 py-3.5 px-6 font-semibold text-sm rounded-xl transition-colors flex items-center justify-center gap-2 ${
                  isFormComplete && !isCreatingPaymentIntent
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isCreatingPaymentIntent ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Preparing Payment...
                  </>
                ) : (
                  <>
                    Continue to Payment
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-4 h-4"
                      style={{ width: '16px', height: '16px' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </>
                )}
              </button>

              {/* Secure Checkout Badge */}
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                  style={{ width: '16px', height: '16px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
                Secure Checkout
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
